import {customElement, property, query} from 'lit/decorators.js';
import type {Model} from './architecture.js';
import {Msg, main} from './architecture.js';
import type {Constructor} from '@gl/lit/types/classes.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import {ImmutableComponent} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  AudienceScheduleStartTypeEnum,
  isAudienceFrequency,
  isAudienceScheduleEndType,
} from '@gl/lit/types/journey/audience_frequency.js';
import {
  convertToDateInputValue,
  convertToTimeInputValue,
} from '@gl/lit/utils/dates.js';
import selectStyles from '@gl/lit/components/base/select/style.scss';
import type FlyScheduleFrequencyInput from '@gl/lit/components/base/schedule/frequency/index.js';
import {isFullDateString, isValidoccurrenceEntry} from './utils.js';
import {CAMPAIGN_TYPE} from '@gl/lit/types/audience_exports.js';

import styles from './style.scss';
import {isKnownTimezone} from '@gl/lit/common/constants/timezones.js';
import {ensureArrayValue} from '@gl/lit/utils/arrays.js';
import type FlySelect from '@gl/lit/components/base/select/index.js';
import type {Ref} from 'lit/directives/ref.js';
import {createRef} from 'lit/directives/ref.js';
import {consume} from '@lit-labs/context';
import type {SessionContextData} from '@gl/lit/contexts/session.js';
import {SessionContext} from '@gl/lit/contexts/session.js';

export const SCHEDULE_FREQUENCY_ELEMENT_TAG = 'audience-export-schedule';

/**
 * Handle frequency scheduling with the recurring defaults.
 *
 * @fires FlyAudienceExportSchedule#export-schedule:change
 */
@customElement(SCHEDULE_FREQUENCY_ELEMENT_TAG)
export default class FlyAudienceExportSchedule extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  @property({type: Boolean})
  exportedAudience = false;

  @property({
    type: String,
    attribute: 'campaign-type',
    converter(value) {
      return value === CAMPAIGN_TYPE.ONE_TIME
        ? CAMPAIGN_TYPE.ONE_TIME
        : CAMPAIGN_TYPE.ONGOING;
    },
  })
  get campaignType() {
    return this.model.campaignType;
  }
  set campaignType(value) {
    this.handleUpdate(Msg.setCampaignType(value));
  }

  @property({
    type: String,
    converter(value) {
      return isAudienceFrequency(value) ? value : '';
    },
  })
  get frequency() {
    return this.model.frequency ?? '';
  }
  set frequency(value) {
    this.handleUpdate(Msg.setFrequency(value !== '' ? value : null));
  }

  @property({
    type: String,
    attribute: 'start-type',
    converter(value) {
      return AudienceScheduleStartTypeEnum.SCHEDULE === value
        ? AudienceScheduleStartTypeEnum.SCHEDULE
        : AudienceScheduleStartTypeEnum.NOW;
    },
  })
  get startType() {
    return this.model.startType;
  }
  set startType(value) {
    this.handleUpdate(Msg.setStartType(value));
  }

  @property({
    type: String,
    attribute: 'start-date',
    converter(value) {
      return isFullDateString(value) ? value : '';
    },
  })
  get startDate() {
    const {startDate, startTime} = this.model;
    return startDate === null || startTime === null
      ? ''
      : `${startDate}T${startTime}`;
  }
  set startDate(value) {
    const date = convertToDateInputValue(value);
    const time = convertToTimeInputValue(value);
    this.handleUpdate(Msg.setStartDate(date));
    this.handleUpdate(Msg.setStartTime(time));
  }

  @property({
    type: String,
    attribute: 'start-timezone',
    converter(value) {
      return isKnownTimezone(value ?? '') ? value : '';
    },
  })
  get startTimezone() {
    return this.model.startTimezone?.name ?? '';
  }
  set startTimezone(value) {
    this.handleUpdate(Msg.setStartTimezone(value));
  }

  @property({
    type: String,
    attribute: 'end-type',
    converter(value) {
      return isAudienceScheduleEndType(value) ? value : '';
    },
  })
  get endType() {
    return this.model.endType ?? '';
  }
  set endType(value) {
    this.handleUpdate(Msg.setEndType(value));
  }

  @property({
    type: String,
    attribute: 'end-date',
    converter(value) {
      return isFullDateString(value) ? value : '';
    },
  })
  get endDate() {
    const {endDate, endTime} = this.model;
    return endDate === null || endTime === null ? '' : `${endDate}T${endTime}`;
  }
  set endDate(value) {
    this.handleUpdate(Msg.setEndDate(convertToDateInputValue(value)));
    this.handleUpdate(Msg.setEndTime(convertToTimeInputValue(value)));
  }

  @property({
    type: String,
    attribute: 'end-timezone',
    converter(value) {
      return isKnownTimezone(value ?? '') ? value : '';
    },
  })
  get endTimezone() {
    return this.model.endTimezone?.name ?? '';
  }
  set endTimezone(value) {
    this.handleUpdate(Msg.setEndTimezone(value));
  }

  @property({
    type: Array,
  })
  get occurrence() {
    return this.model.occurrence;
  }
  set occurrence(data) {
    data = ensureArrayValue(data);
    const occurrence = data.filter(isValidoccurrenceEntry);
    this.handleUpdate(Msg.setOccurrence(occurrence));
  }

  $monthlyOptionsSelect: Ref<FlySelect> = createRef();

  @property({type: Boolean})
  get disabled() {
    return this.model.disabled;
  }
  set disabled(value) {
    this.handleUpdate(Msg.setDisabled(value));
  }

  @query('fly-schedule-frequency')
  $frequency?: FlyScheduleFrequencyInput;

  static get styles() {
    return [styles, selectStyles];
  }

  @consume({context: SessionContext, subscribe: true})
  @property({type: Object})
  session?: SessionContextData;
}

declare global {
  interface HTMLElementTagNameMap {
    [SCHEDULE_FREQUENCY_ELEMENT_TAG]: FlyAudienceExportSchedule;
  }
}
